import produce from "immer";

import { OperationMode, Stream } from "@/apollo/types";

import { hasFullSyncToReconcileAgainst } from "../fullSyncSchedule";
import { SourceStream, Substream } from "../types";

type StreamCapabilities = Pick<
  Stream,
  "supportedModes" | "configurableIncremental"
>;

export type SourceStreamsState = { [key: string]: SourceStream | undefined };

export type SourceStreamsAction =
  | {
      type: "set_source_streams";
      payload: {
        streams: Stream[];
      };
    }
  | {
      type: "toggle_full_sync";
      payload: {
        stream: Stream;
      };
    }
  | {
      type: "disable_incremental_sync";
      payload: {
        stream: Stream;
      };
    }
  | {
      type: "toggle_incremental_sync";
      payload: (
        { mode: "MERGE"; primaryKeys: string[] } | { mode: "APPEND" }
      ) & {
        stream: Stream;
        cursor: string;
        additionalCursors?: string[];
      };
    }
  | {
      type: "set_incremental_config";
      payload: {
        cursor?: string;
        primaryKeys: string[];
        sourceStreamName: string;
        stream: StreamCapabilities;
      };
    }
  | {
      type: "change_excluded_column";
      payload: {
        columnName: string;
        sourceStreamName: string;
        checked: boolean;
      };
    }
  | {
      type: "change_hashed_column";
      payload: {
        columnName: string;
        sourceStreamName: string;
        hashed: boolean;
      };
    }
  | {
      type: "set_source_stream";
      payload: {
        checked: boolean;
        stream: Stream;
      };
    }
  | {
      type: "set_full_sync_schedule";
      payload: {
        sourceStreamName: string;
        stream: Stream;
        cron: string | null;
      };
    }
  | {
      type: "toggle_protected_from_full_sync";
      payload: { sourceStreamName: string };
    }
  | {
      type: "toggle_change_data_capture";
      payload: { sourceStreamName: string; stream: Stream };
    }
  | {
      type: "toggle_substream";
      payload: {
        sourceStreamName: string;
        substreamId: string;
        checked: boolean;
      };
    }
  | {
      type: "toggle_history_table";
      payload: { sourceStreamName: string };
    }
  | {
      type: "toggle_soft_delete";
      payload: { sourceStreamName: string };
    }
  | {
      type: "set_history_table_all";
      payload: { enabled: boolean };
    }
  | {
      type: "reset_streams";
      payload: { availableStreamNames: string[] };
    };

/**
 * Capturing deletes is reconciled on a full sync, so the backend refuses it without one. Any edit
 * that takes the last full sync away has to turn it off here too, or the sync cannot be saved.
 */
const reconcileSoftDelete = (
  stream: StreamCapabilities,
  streamState: SourceStream,
) => {
  if (
    streamState.softDeleteEnabled &&
    !hasFullSyncToReconcileAgainst(stream, streamState)
  ) {
    streamState.softDeleteEnabled = false;
  }
};

const validateState = (stream: Stream, streamState: SourceStream) => {
  if (
    stream.configurableIncremental &&
    streamState?.mode !== "FULL" &&
    !streamState?.incrementalPointerId.length &&
    !streamState?.changeDataCaptureEnabled
  ) {
    // When incremental sync is configurable, but it's empty
    streamState.isIncrementalInvalid = true;
    return streamState;
  }

  streamState.isIncrementalInvalid = false;
  return streamState;
};

const createInitialSourceStreamState = (stream: Stream): SourceStream => {
  const state = {
    primaryKeys: [],
    incrementalPointerId: "",
    additionalIncrementalPointerIds: [],
    hashedColumns: [],
    excludedColumns: [],
    excludedSubstreams: stream.excludedSubstreams ?? [],
    enabledHistoryTable: stream.enabledHistoryTable ?? false,
    softDeleteEnabled: stream.softDeleteEnabled ?? false,
    fullSyncSchedule: null,
    substreams: {},
    fullSyncAlways: false,
    protectedFromFullSync: false,
    changeDataCaptureEnabled: false,
    mode: stream.supportedModes.includes(OperationMode.Incremental)
      ? "MERGE"
      : "FULL",
    ...stream.defaultConfig,
    selectable: stream.selectable,
  };
  return validateState(stream, state);
};

export function sourceStreamsReducer(
  state: SourceStreamsState,
  action: SourceStreamsAction,
) {
  switch (action.type) {
    case "set_source_streams": {
      return produce(state, (draft) => {
        action.payload.streams.forEach((stream) => {
          const existing = draft[stream.name];
          if (existing) {
            // Saved state meets what the source can do only here, so this is the first point a
            // stream loaded with capture deletes but no full sync can be seen for what it is.
            reconcileSoftDelete(stream, existing);
            return;
          }
          draft[stream.name] = {
            ...createInitialSourceStreamState(stream),
            selectable: stream.selectable,
            substreams: stream.substreams.reduce<{ [key: string]: Substream }>(
              (prev, cur) => {
                prev[cur.destinationTableName] = {
                  hashedColumns: [],
                  excludedColumns: [],
                };
                return prev;
              },
              {},
            ),
          };
        });
        Object.keys(draft).forEach((sourceStreamName) => {
          if (
            !action.payload.streams.some(
              (value) => value.name === sourceStreamName,
            )
          ) {
            delete draft[sourceStreamName];
          }
        });
      });
    }
    case "set_source_stream": {
      return produce(state, (draft) => {
        const exists = draft[action.payload.stream.name];
        if (!action.payload.checked && exists) {
          delete draft[action.payload.stream.name];
          return;
        }
        if (exists) return;
        draft[action.payload.stream.name] = {
          ...createInitialSourceStreamState(action.payload.stream),
          substreams: action.payload.stream.substreams.reduce<{
            [key: string]: Substream;
          }>((prev, cur) => {
            prev[cur.destinationTableName] = {
              hashedColumns: [],
              excludedColumns: [],
            };
            return prev;
          }, {}),
        };
      });
    }
    case "toggle_full_sync": {
      return produce(state, (draft) => {
        const streamState = draft[action.payload.stream.name];
        if (!streamState) {
          throw new Error("No stream" + action.payload.stream.name);
        }
        streamState.fullSyncAlways = !streamState.fullSyncAlways;
        if (streamState.fullSyncAlways) {
          streamState.incrementalPointerId = "";
          streamState.additionalIncrementalPointerIds = [];
          streamState.primaryKeys = [];
          streamState.mode = "FULL";
          streamState.protectedFromFullSync = false;
          // Every run is a full sync now, so a schedule has nothing left to promote.
          streamState.fullSyncSchedule = null;
        } else {
          streamState.mode = "MERGE";
        }

        reconcileSoftDelete(action.payload.stream, streamState);
        validateState(action.payload.stream, streamState);
      });
    }
    case "disable_incremental_sync": {
      return produce(state, (draft) => {
        const streamState = draft[action.payload.stream.name];
        if (!streamState) {
          throw new Error("No stream" + action.payload.stream.name);
        }
        streamState.primaryKeys = [];
        streamState.incrementalPointerId = "";
        streamState.additionalIncrementalPointerIds = [];
        streamState.fullSyncAlways = true;
        streamState.mode = "FULL";

        validateState(action.payload.stream, streamState);
      });
    }
    case "toggle_incremental_sync": {
      return produce(state, (draft) => {
        const streamState = draft[action.payload.stream.name];
        if (!streamState) {
          throw new Error("No stream" + action.payload.stream.name);
        }
        streamState.fullSyncAlways = false;
        streamState.additionalIncrementalPointerIds =
          action.payload.additionalCursors ?? [];
        if (action.payload.mode === "MERGE") {
          streamState.mode = "MERGE";
          streamState.primaryKeys = action.payload.primaryKeys;
          streamState.incrementalPointerId = action.payload.cursor;
        } else {
          streamState.mode = "APPEND";
          streamState.primaryKeys = [];
          streamState.incrementalPointerId = action.payload.cursor;
        }

        reconcileSoftDelete(action.payload.stream, streamState);
        validateState(action.payload.stream, streamState);
      });
    }
    case "set_full_sync_schedule": {
      return produce(state, (draft) => {
        const streamState = draft[action.payload.sourceStreamName];
        if (!streamState)
          throw new Error("No stream" + action.payload.sourceStreamName);
        streamState.fullSyncSchedule = action.payload.cron;
        if (action.payload.cron) {
          streamState.protectedFromFullSync = false;
        }
        reconcileSoftDelete(action.payload.stream, streamState);
      });
    }
    case "toggle_protected_from_full_sync": {
      return produce(state, (draft) => {
        const stream = draft[action.payload.sourceStreamName];
        if (!stream)
          throw new Error(`No stream '${action.payload.sourceStreamName}'`);
        stream.protectedFromFullSync = !stream.protectedFromFullSync;
      });
    }
    case "toggle_change_data_capture": {
      return produce(state, (draft) => {
        const stream = draft[action.payload.sourceStreamName];
        if (!stream) {
          throw new Error(`No stream '${action.payload.sourceStreamName}'`);
        }

        stream.changeDataCaptureEnabled = !stream.changeDataCaptureEnabled;
        if (stream.changeDataCaptureEnabled) {
          stream.primaryKeys = [];
          stream.incrementalPointerId = "";
          stream.additionalIncrementalPointerIds = [];
          stream.fullSyncAlways = false;
          stream.mode = "FULL";
          stream.protectedFromFullSync = true;
          stream.isIncrementalInvalid = false;
        } else {
          stream.fullSyncAlways = false;
          stream.mode = "MERGE";
        }
        reconcileSoftDelete(action.payload.stream, stream);
        validateState(action.payload.stream, stream);
      });
    }
    case "set_incremental_config": {
      return produce(state, (draft) => {
        const stream = draft[action.payload.sourceStreamName];
        if (!stream)
          throw new Error(`No stream '${action.payload.sourceStreamName}'`);
        stream.primaryKeys = action.payload.primaryKeys;
        if (action.payload.cursor) {
          stream.incrementalPointerId = action.payload.cursor;
          stream.isIncrementalInvalid = false;
        }
        reconcileSoftDelete(action.payload.stream, stream);
      });
    }

    case "change_excluded_column": {
      return produce(state, (draft) => {
        const stream = draft[action.payload.sourceStreamName];
        if (!stream)
          throw new Error(`No stream '${action.payload.sourceStreamName}'`);
        if (!action.payload.checked) {
          stream.excludedColumns = [
            ...new Set([...stream.excludedColumns, action.payload.columnName]),
          ];
          stream.hashedColumns = stream.hashedColumns.filter(
            (c) => c !== action.payload.columnName,
          );
        } else {
          stream.excludedColumns = stream.excludedColumns.filter(
            (c) => c !== action.payload.columnName,
          );
        }
      });
    }

    case "change_hashed_column": {
      return produce(state, (draft) => {
        const stream = draft[action.payload.sourceStreamName];
        if (!stream)
          throw new Error(`No stream '${action.payload.sourceStreamName}'`);
        if (!action.payload.hashed) {
          stream.hashedColumns = stream.hashedColumns.filter(
            (c) => c !== action.payload.columnName,
          );
        } else {
          stream.hashedColumns = [
            ...new Set([...stream.hashedColumns, action.payload.columnName]),
          ];
        }
      });
    }
    case "toggle_substream": {
      return produce(state, (draft) => {
        const stream = draft[action.payload.sourceStreamName];
        if (!stream)
          throw new Error(`No stream '${action.payload.sourceStreamName}'`);
        if (action.payload.checked) {
          stream.excludedSubstreams = stream.excludedSubstreams.filter(
            (id) => id !== action.payload.substreamId,
          );
        } else {
          stream.excludedSubstreams = [
            ...new Set([
              ...stream.excludedSubstreams,
              action.payload.substreamId,
            ]),
          ];
        }
      });
    }
    case "toggle_history_table": {
      return produce(state, (draft) => {
        const stream = draft[action.payload.sourceStreamName];
        if (!stream)
          throw new Error(`No stream '${action.payload.sourceStreamName}'`);
        stream.enabledHistoryTable = !stream.enabledHistoryTable;
      });
    }
    case "toggle_soft_delete": {
      return produce(state, (draft) => {
        const stream = draft[action.payload.sourceStreamName];
        if (!stream)
          throw new Error(`No stream '${action.payload.sourceStreamName}'`);
        stream.softDeleteEnabled = !stream.softDeleteEnabled;
      });
    }
    case "set_history_table_all": {
      return produce(state, (draft) => {
        for (const stream of Object.values(draft)) {
          if (stream) {
            stream.enabledHistoryTable = action.payload.enabled;
          }
        }
      });
    }
    case "reset_streams": {
      return produce(state, (draft) => {
        for (const name of Object.keys(draft)) {
          if (!action.payload.availableStreamNames.includes(name)) {
            delete draft[name];
          }
        }
      });
    }
    default: {
      return state;
    }
  }
}
